import axios, { Canceler } from 'axios';
import React from 'react';

import { HtmlContent, LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterUtils,
    ApiResponse,
    BuilderChoiceOptionContract,
    CharacterPreferences,
    ChoiceData,
    ChoiceUtils,
    Constants,
    Feat,
    FeatDefinitionContract,
    FeatLookup,
    FeatUtils,
    HelperUtils,
    HtmlSelectOption,
    PrerequisiteData,
    PrerequisiteUtils,
    RacialTrait,
    RacialTraitUtils,
    OptionalOriginLookup,
    OptionalOriginUtils,
    DefinitionPool,
    CoreUtils,
} from '@dndbeyond/character-rules-engine/es';

import { Collapsible, CollapsibleHeader } from '../../../../../Shared/components/legacy/common/Collapsible';
import DataLoadingStatusEnum from '../../../../../Shared/constants/DataLoadingStatusEnum';
import { DetailChoice, DetailChoiceFeat } from '../../../../../Shared/containers/DetailChoice';
import { AppLoggerUtils } from '../../../../../Shared/utils';

interface Props {
    racialTrait: RacialTrait;
    choiceInfo: ChoiceData;
    preferences: CharacterPreferences;
    prerequisiteData: PrerequisiteData;
    featLookup: FeatLookup;
    onChoiceChange: (racialTraitId: number, choiceId: string, type: number, value: any) => void;
    loadAvailableFeats: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<FeatDefinitionContract>>>;
    optionalOriginLookup: OptionalOriginLookup;
    definitionPool: DefinitionPool;
    raceName?: string | null;
}
interface State {
    featData: Array<Feat>;
    hasFeatChoice: boolean;
    collapsibleOpened: boolean;
    loadingStatus: DataLoadingStatusEnum;
}
export default class RaceManageRacialTrait extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            featData: [],
            hasFeatChoice: false,
            collapsibleOpened: false,
            loadingStatus: DataLoadingStatusEnum.NOT_INITIALIZED,
        };
    }

    loadFeatsCanceler: null | Canceler = null;

    componentDidMount(): void {
        this.setState(
            {
                hasFeatChoice: this.hasFeatChoice(this.props),
            },
            () => {
                this.conditionallyLoadFeatData();
            },
        );
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { racialTrait } = this.props;
        const { collapsibleOpened } = this.state;

        if (racialTrait !== prevProps.racialTrait) {
            this.setState(
                {
                    hasFeatChoice: this.hasFeatChoice(this.props),
                },
                () => {
                    this.conditionallyLoadFeatData();
                },
            );
        }
        if (collapsibleOpened && !prevState.collapsibleOpened) {
            this.conditionallyLoadFeatData();
        }
    }

    componentWillUnmount(): void {
        if (this.loadFeatsCanceler !== null) {
            this.loadFeatsCanceler();
        }
    }

    conditionallyLoadFeatData = (): void => {
        const { loadAvailableFeats } = this.props;
        const { hasFeatChoice, loadingStatus, collapsibleOpened } = this.state;

        if (hasFeatChoice && collapsibleOpened && loadingStatus === DataLoadingStatusEnum.NOT_INITIALIZED) {
            this.setState({
                loadingStatus: DataLoadingStatusEnum.LOADING,
            });

            loadAvailableFeats({
                cancelToken: new axios.CancelToken((c) => {
                    this.loadFeatsCanceler = c;
                }),
            })
                .then((response) => {
                    let featData: Array<Feat> = [];

                    const data = ApiAdapterUtils.getResponseData(response);
                    if (data !== null) {
                        featData = data.map((definition) => FeatUtils.simulateFeat(definition));
                    }

                    this.setState({
                        featData,
                        loadingStatus: DataLoadingStatusEnum.LOADED,
                    });
                    this.loadFeatsCanceler = null;
                })
                .catch(AppLoggerUtils.handleAdhocApiError);
        }
    };

    getFeatData = (existingFeatId: number | null): Array<Feat> => {
        const { featData } = this.state;
        const { featLookup } = this.props;

        let data: Array<Feat> = [...featData];

        if (existingFeatId !== null) {
            let existingFeat = HelperUtils.lookupDataOrFallback(featLookup, existingFeatId);
            if (existingFeat !== null && !data.some((feat) => FeatUtils.getId(feat) === existingFeatId)) {
                data.push(existingFeat);
            }
        }

        return data;
    };

    hasFeatChoice = (props: Props): boolean => {
        const { racialTrait } = props;

        const choices = RacialTraitUtils.getChoices(racialTrait);
        return choices.some(
            (choice) => ChoiceUtils.getType(choice) === Constants.BuilderChoiceTypeEnum.FEAT_CHOICE_OPTION,
        );
    };

    handleCollapsibleOpened = (): void => {
        this.setState({
            collapsibleOpened: true,
        });
    };

    handleChoiceChange = (choiceId: string, type: number, subType: number | null, value: any): void => {
        const { onChoiceChange, racialTrait } = this.props;

        if (onChoiceChange) {
            onChoiceChange(RacialTraitUtils.getId(racialTrait), choiceId, type, HelperUtils.parseInputInt(value));
        }
    };

    renderHeader = (): React.ReactNode => {
        const { racialTrait, optionalOriginLookup, definitionPool, raceName } = this.props;
        let name = RacialTraitUtils.getName(racialTrait);
        const displayConfiguration = RacialTraitUtils.getDisplayConfiguration(racialTrait);
        const addName = displayConfiguration
            ? CoreUtils.getDisplayConfigurationValue(
                  Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
                  displayConfiguration,
              ) === Constants.DisplayConfigurationValueEnum.ON
            : false;
        if (raceName && addName) {
            // TODO get race name
            name = `${name} (${raceName})`;
        }

        const choices = RacialTraitUtils.getChoices(racialTrait);

        let metaItems: Array<string> = [];
        if (choices.length) {
            metaItems.push(`${choices.length} Choice${choices.length !== 1 ? 's' : ''}`);
        }

        const featureType = RacialTraitUtils.getFeatureType(racialTrait);

        const addOrigin = displayConfiguration
            ? CoreUtils.getDisplayConfigurationValue(
                  Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
                  displayConfiguration,
              ) === Constants.DisplayConfigurationValueEnum.OFF
            : false;

        if (featureType !== Constants.FeatureTypeEnum.GRANTED || addOrigin) {
            metaItems.push('Origin');
        }
        if (featureType === Constants.FeatureTypeEnum.REPLACEMENT) {
            name = `Origin ${name}`;

            const optionalOrigin = HelperUtils.lookupDataOrFallback(
                optionalOriginLookup,
                RacialTraitUtils.getDefinitionKey(racialTrait),
            );

            if (optionalOrigin) {
                const affectedDefinitionKey = OptionalOriginUtils.getAffectedRacialTraitDefinitionKey(optionalOrigin);
                if (affectedDefinitionKey) {
                    const replacedRacialTrait = RacialTraitUtils.simulateRacialTrait(
                        affectedDefinitionKey,
                        definitionPool,
                    );

                    if (replacedRacialTrait) {
                        metaItems.push(`Replaces ${RacialTraitUtils.getName(replacedRacialTrait)}`);
                    }
                }
            }
        }

        return <CollapsibleHeader metaItems={metaItems} heading={name} />;
    };

    renderChoices = (): React.ReactNode => {
        const { choiceInfo, prerequisiteData, preferences, racialTrait, featLookup } = this.props;

        const choices = RacialTraitUtils.getChoices(racialTrait);

        if (choices === null) {
            return null;
        }

        let subchoicesNode: React.ReactNode;

        return (
            <React.Fragment>
                {choices.map((choice) => {
                    const optionValue = ChoiceUtils.getOptionValue(choice);
                    const options = ChoiceUtils.getOptions(choice);
                    const type = ChoiceUtils.getType(choice);
                    const id = ChoiceUtils.getId(choice);

                    let availableOptions: Array<HtmlSelectOption & BuilderChoiceOptionContract> = [];
                    let detailChoiceDesc: string | null = null;

                    switch (type) {
                        case Constants.BuilderChoiceTypeEnum.FEAT_CHOICE_OPTION:
                            const featData = this.getFeatData(optionValue);
                            availableOptions = featData
                                .filter((feat) => {
                                    if (FeatUtils.getId(feat) === optionValue) {
                                        return true;
                                    }

                                    if (
                                        HelperUtils.lookupDataOrFallback(featLookup, FeatUtils.getId(feat)) === null &&
                                        (!preferences.enforceFeatRules ||
                                            PrerequisiteUtils.validatePrerequisiteGrouping(
                                                FeatUtils.getPrerequisites(feat),
                                                prerequisiteData,
                                            ))
                                    ) {
                                        return true;
                                    }

                                    return false;
                                })
                                .map((feat) => ({
                                    value: FeatUtils.getId(feat),
                                    label: FeatUtils.getName(feat),
                                    description: '',
                                    id: FeatUtils.getId(feat),
                                }));

                            if (optionValue !== null) {
                                const chosenFeat = featData.find((feat) => FeatUtils.getId(feat) === optionValue);
                                if (chosenFeat) {
                                    detailChoiceDesc = FeatUtils.getDescription(chosenFeat);
                                    subchoicesNode = <DetailChoiceFeat featId={optionValue} />;
                                }
                            }
                            break;
                        default:
                            availableOptions = options.map((option) => ({
                                ...option,
                                value: option.id,
                            }));
                    }

                    return (
                        <React.Fragment key={id ? id : ''}>
                            <DetailChoice
                                {...choice}
                                choice={choice}
                                options={availableOptions}
                                onChange={this.handleChoiceChange}
                                description={detailChoiceDesc ? detailChoiceDesc : ''}
                                choiceInfo={choiceInfo}
                                showBackgroundProficiencyOptions={true}
                            />
                            {subchoicesNode}
                        </React.Fragment>
                    );
                })}
            </React.Fragment>
        );
    };

    render() {
        const { racialTrait } = this.props;
        const { hasFeatChoice, loadingStatus } = this.state;

        const description = RacialTraitUtils.getDescription(racialTrait);
        const choices = RacialTraitUtils.getChoices(racialTrait);

        const hasTodoChoices: boolean = choices.some((choice) => ChoiceUtils.getOptionValue(choice) === null);

        const conClsNames: Array<string> = ['race-detail-racial-trait-name'];
        if (hasTodoChoices) {
            conClsNames.push('collapsible-todo');
        }

        let contentNode: React.ReactNode;
        if (hasFeatChoice) {
            if (loadingStatus === DataLoadingStatusEnum.LOADED) {
                contentNode = this.renderChoices();
            } else {
                contentNode = <LoadingPlaceholder />;
            }
        } else {
            contentNode = this.renderChoices();
        }

        return (
            <Collapsible trigger={this.renderHeader()} clsNames={conClsNames} onOpened={this.handleCollapsibleOpened}>
                <HtmlContent className="race-detail-racial-trait-description" html={description ? description : ''} />
                {contentNode}
            </Collapsible>
        );
    }
}
